import { isObject } from 'lodash';
import { ChannelEntityTemplateDto, ChannelEnum } from '@c2b/muven-commons';
import { StoreModel } from '../../store/models/StoreModel';
import { ChannelStoreModel } from './ChannelStoreModel';
import { ChannelAllowExportEnum } from "@c2b/muven-commons"

interface StoreWithOnlyId {
    id: string;
}

export class ChannelModel {
    id?:string = undefined
    name:string = ''
    idExternal?: string;
    isActive:boolean = false
    allowExport:ChannelAllowExportEnum = ChannelAllowExportEnum.All
    enumChannel:ChannelEnum
    credentialsByStore: boolean = false
    availableStockPercentage?: number
    pricePercentage?: number
    priceDecimalsPreference?: number
    storeChannels: ChannelStoreModel[] = []
    stockStores: null|string[] = []
    priceManagementStore: null|StoreWithOnlyId|StoreModel = new StoreModel()
    orderManagementStore: null|StoreWithOnlyId|StoreModel = new StoreModel()
    showWithoutStock:boolean = false
    entityTemplate: ChannelEntityTemplateDto|ChannelEntityTemplateDto[]|null = null
    loadingListAction:boolean = false;
    idPriceManagementStore?: number;

    constructor(c?:Partial<ChannelModel>) {
        Object.assign(this, c)

        this._parseStoreChannels()
        this._parsePriceManagementStore()
        this._parseOrderManagementStore()
        this._parseStockStores()
        this._normalizeEntityTemplate()
    }

    getRealName():string {       
        if (this.isTray()) {
            return 'Tray'
        }

        if (this.isTrayFbits()) {
            return 'Tray by Fbits'
        }

        if (this.isAzata()) {
            return 'Azata'
        }

        if (this.isB2W()) {
            return 'B2W'
        }

        if (this.isJetNeo()) {
            return 'JetNeo'
        }

        if (this.isLojaIntegrada()) {
            return 'Loja Integrada'
        }

        if (this.isMagazineLuiza()) {
            return 'Magazine Luiza'
        }

        if (this.isMagentoV19()) {
            return 'Magento v1.9'
        }

        if (this.isMagentoV23()) {
            return 'Magento v2.3'
        }

        if (this.isMercadoLivre()) {
            return 'Mercado Livre'
        }

        if (this.isMuvenCart()) {
            return 'Muven Cart'
        }

        if (this.isNuvemShop()) {
            return 'NuvemShop'
        }

        if (this.isViaVarejo()) {
            return 'Via Varejo'
        }

        if (this.isVtex()) {
            return 'VTex'
        }

        if (this.isWooCommerce()) {
            return 'WooCommerce'
        }

        if (this.isOpenCart()) {
            return 'Open Cart'
        }

        if (this.isShopify()) {
            return 'Shopify'
        }

        if (this.isNetshoes()) {
            return 'Netshoes'
        }

        if (this.isDafiti()) {
            return 'Dafiti'
        }

        if (this.isFlexy()) {
            return 'Flexy'
        }

        if (this.isCustom()) {
            return this.name
        }

        if (this.isShopee()) {
            return 'Shopee'
        }

        if (this.isWBuy()) {
            return 'WBuy'
        }

        if (this.isShein()) {
            return 'Shein'
        }

        if (this.isTiktokShop()) {
            return 'TikTok Shop'
        }

        // if (this.isAmazon()) {
        //     return 'Amazon'
        // }

        return ''
    }

    hasCustomizedName():boolean {
        if (!this.enumChannel) {
            return false
        }

        return this.name?.toLowerCase()?.trim() !== this.getRealName().toLowerCase().trim()
    }

    isMagentoV19():boolean {
        return this.enumChannel === ChannelEnum.Magento_1_9
    }

    isMagentoV23():boolean {
        return this.enumChannel === ChannelEnum.Magento_2_3
    }

    isVtex():boolean {
        return this.enumChannel === ChannelEnum.VTex
    }

    isTray():boolean {
        return this.enumChannel === ChannelEnum.Tray
    }

    isTrayFbits():boolean {
        return this.enumChannel === ChannelEnum.TrayFbits
    }

    isMuvenCart():boolean {
        return this.enumChannel === ChannelEnum.Muven_Cart;
    }

    isB2W():boolean {
        return this.enumChannel === ChannelEnum.B2W;
    }

    isLojaIntegrada():boolean {
        return this.enumChannel === ChannelEnum.LojaIntegrada;
    }

    isMagazineLuiza():boolean {
        return this.enumChannel === ChannelEnum.MagazineLuiza;
    }

    isViaVarejo():boolean {
        return this.enumChannel === ChannelEnum.ViaVarejo;
    }

    isAzata():boolean {
        return this.enumChannel === ChannelEnum.Azata;
    }

    isMercadoLivre():boolean {
        return this.enumChannel === ChannelEnum.MercadoLivre;
    }

    isWooCommerce():boolean {
        return this.enumChannel === ChannelEnum.WooCommerce;
    }

    isJetNeo():boolean {
        return this.enumChannel === ChannelEnum.JetNeo;
    }

    isNuvemShop():boolean {
        return this.enumChannel === ChannelEnum.NuvemShop;
    }

    isOpenCart():boolean {
        return this.enumChannel === ChannelEnum.OpenCart;
    }

    isShopify():boolean {
        return this.enumChannel === ChannelEnum.Shopify;
    }

    isShopee():boolean {
        return this.enumChannel === ChannelEnum.Shopee;
    }

    isNetshoes():boolean {
        return this.enumChannel === ChannelEnum.Netshoes;
    }

    isDafiti():boolean {
        return this.enumChannel === ChannelEnum.Dafiti;
    }

    isFlexy():boolean {
        return this.enumChannel === ChannelEnum.Flexy;
    }

    isWBuy():boolean {
        return this.enumChannel === ChannelEnum.WBuy;
    }

    isShein():boolean {
        return this.enumChannel === ChannelEnum.Shein;
    }

    isTiktokShop():boolean {
        return this.enumChannel === ChannelEnum.TiktokShop;
    }

    // isAmazon():boolean {
    //     return this.enumChannel === ChannelEnum.Amazon;
    // }

    isCustom():boolean {
        return this.enumChannel === ChannelEnum.Custom;
    }

    getAvailableStockPercentage():number {
        if (!this.availableStockPercentage) {
            return 100
        }

        return this.availableStockPercentage
    }

    getPricePercentage():number {
        if (!this.pricePercentage) {
            return 100
        }

        return this.pricePercentage
    }

    isAllowExportAll():boolean {
        return this.allowExport === ChannelAllowExportEnum.All
    }

    isAllowExportStock():boolean {
        return this.allowExport === ChannelAllowExportEnum.Stock
    }

    isAllowExportStockAndPrice():boolean {
        return this.allowExport === ChannelAllowExportEnum.StockAndPrice
    }

    isAllowExportInsertProductAndUpdateStockAndPrice():boolean {
        return this.allowExport === ChannelAllowExportEnum.InsertProductAndUpdateStockAndPrice
    }

    isAllowExportInsertProductAndUpdateStock():boolean {
        return this.allowExport === ChannelAllowExportEnum.InsertProductAndUpdateStock
    }

    allowsMultiAssociation(): boolean {
        return this.idExternal === 'multi';
    }

    hasApiLimitReq(): boolean {
        if (this.isLojaIntegrada()) {
            return true;
        }

        return false;
    }

    private _parseStoreChannels():void {
        this.storeChannels = (this.storeChannels || []).map((sc) => {
            return new ChannelStoreModel(sc);
        })
    }

    private _parseStockStores():void {
        this.stockStores = this.stockStores || []
    }

    private _parsePriceManagementStore():void {
        this.priceManagementStore = new StoreModel(this.priceManagementStore as StoreModel)
    }

    private _parseOrderManagementStore():void {
        this.orderManagementStore = new StoreModel(this.orderManagementStore as StoreModel)
    }

    private _normalizeEntityTemplate():void {
        if (!Array.isArray(this.entityTemplate) && isObject(this.entityTemplate)) {
            this.entityTemplate = [this.entityTemplate as ChannelEntityTemplateDto]
        }
    }
}
